import { useEffect, useRef, useState } from "react";
import { CircleCheck, Package, CalendarHeart, Truck, Star } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { getClubPlans, clubSubscribe, formatPrice } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const HOW_IT_WORKS = [
    { icon: Package, title: "Choose your membership", text: "Three tiers, one promise — the finest British dried beef, every month." },
    { icon: CalendarHeart, title: "We curate each month", text: "Our head butcher selects the best batches from the five farms as they come off the racks." },
    { icon: Truck, title: "Delivered, on us", text: "Complimentary delivery to your door, every month. Pause or cancel anytime." },
];

export default function Club() {
    const [plans, setPlans] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "" });
    const [submitting, setSubmitting] = useState(false);
    const [membership, setMembership] = useState(null);
    const formRef = useRef(null);

    useEffect(() => {
        getClubPlans()
            .then((p) => {
                setPlans(p);
                const highlighted = p.find((x) => x.highlight);
                if (highlighted) setSelectedPlan(highlighted.id);
            })
            .catch(() => setPlans([]));
    }, []);

    const choosePlan = (planId) => {
        setSelectedPlan(planId);
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedPlan) {
            toast.error("Please choose a membership tier first.");
            return;
        }
        setSubmitting(true);
        try {
            const sub = await clubSubscribe({ plan_id: selectedPlan, ...form });
            setMembership(sub);
        } catch (err) {
            if (err?.response?.status === 409) {
                toast.error("This email already has an active membership.");
            } else {
                toast.error("We couldn't complete your membership. Please check your details.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const inputClass =
        "w-full bg-white/[0.06] border border-white/15 px-4 py-3.5 text-sm rounded-sm text-[#F6F1E7] placeholder:text-white/30 focus:outline-none focus:border-[#C9B37A] transition-colors";

    return (
        <main className="bg-[#0B0B0C] text-[#F6F1E7] min-h-screen pb-24" data-testid="club-page">
            {/* Hero */}
            <section className="relative h-[70vh] min-h-[480px] overflow-hidden flex items-end">
                <img src="/generated/club-hero.jpg" alt="A British Beef Club monthly delivery" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/40 to-[#0B0B0C]/30" />
                <div className="relative z-10 max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 pb-16 w-full">
                    <p className="eyebrow text-[#C9B37A]">Membership</p>
                    <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-medium mt-3 leading-tight">
                        The British
                        <br />
                        <span className="italic font-normal">Beef Club.</span>
                    </h1>
                    <p className="mt-5 text-sm text-white/60 max-w-md leading-relaxed">
                        Britain's finest dried beef, delivered monthly. Member pricing, rare releases first, and the
                        occasional invitation to the farms themselves.
                    </p>
                </div>
            </section>

            {/* How it works */}
            <section className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 py-16 lg:py-20">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
                    {HOW_IT_WORKS.map((step, i) => (
                        <Reveal key={step.title} delay={i * 0.08} className="text-center">
                            <step.icon size={28} strokeWidth={1.1} className="text-[#C9B37A] mx-auto" />
                            <h3 className="font-serif-display text-2xl font-medium mt-4">{step.title}</h3>
                            <p className="text-sm text-white/50 mt-3 leading-relaxed max-w-xs mx-auto">{step.text}</p>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* Tiers */}
            <section className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12" data-testid="club-tiers">
                {!plans ? (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {[...Array(3)].map((_, i) => (
                            <Skeleton key={i} className="h-[430px] rounded-xl bg-white/[0.05]" />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                        {plans.map((plan, i) => (
                            <Reveal key={plan.id} delay={i * 0.1}>
                                <div
                                    className={`relative flex flex-col rounded-xl p-8 h-full border transition-colors duration-300 ${
                                        plan.highlight
                                            ? "bg-[#0F2A1F] border-[#C9B37A]/50"
                                            : "bg-white/[0.03] border-white/10 hover:border-white/25"
                                    }`}
                                    data-testid={`club-plan-${plan.id}`}
                                >
                                    {plan.highlight && (
                                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 font-mono-meta text-[9px] tracking-[0.2em] px-3 py-1 bg-[#C9B37A] text-[#0B0B0C] rounded-sm whitespace-nowrap">
                                            MOST POPULAR
                                        </span>
                                    )}
                                    <h3 className="font-serif-display text-3xl font-medium">{plan.name}</h3>
                                    <p className="text-xs text-white/50 mt-2 leading-relaxed min-h-[2rem]">{plan.tagline}</p>
                                    <div className="mt-6 flex items-baseline gap-2">
                                        <span className="font-serif-display text-5xl font-medium text-[#C9B37A]">
                                            {formatPrice(plan.price_monthly)}
                                        </span>
                                        <span className="font-mono-meta text-[10px] tracking-wide text-white/40">/ MONTH</span>
                                    </div>
                                    <ul className="mt-7 space-y-3 flex-1">
                                        {plan.features.map((f) => (
                                            <li key={f} className="flex items-start gap-2.5 text-sm text-white/65">
                                                <CircleCheck size={15} strokeWidth={1.5} className="text-[#C9B37A] mt-0.5 flex-shrink-0" />
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={() => choosePlan(plan.id)}
                                        className={`btn-luxe w-full mt-8 ${plan.highlight ? "btn-gold" : "btn-outline-light"}`}
                                        data-testid={`club-plan-select-${plan.id}`}
                                    >
                                        Join {plan.name}
                                    </button>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                )}
            </section>

            {/* Signup / success */}
            <section ref={formRef} className="max-w-[640px] mx-auto px-5 sm:px-8 pt-20" data-testid="club-signup-section">
                {membership ? (
                    <Reveal className="text-center bg-[#0F2A1F] border border-[#C9B37A]/40 rounded-xl p-10" data-testid="club-success">
                        <Star size={36} strokeWidth={1.1} className="text-[#C9B37A] mx-auto" />
                        <h2 className="font-serif-display text-4xl font-medium mt-5">
                            Welcome to the Club, {membership.first_name}.
                        </h2>
                        <p className="text-sm text-white/60 mt-4 leading-relaxed">
                            Your <span className="text-[#C9B37A]">{membership.plan_name}</span> membership is confirmed.
                            Your first delivery is being curated now.
                        </p>
                        <div className="inline-block mt-7 border border-white/15 bg-black/20 rounded-lg px-8 py-4">
                            <p className="font-mono-meta text-[9px] tracking-[0.25em] text-white/40">MEMBERSHIP NUMBER</p>
                            <p className="font-serif-display text-2xl font-medium mt-1 text-[#C9B37A]" data-testid="club-membership-number">
                                {membership.membership_number}
                            </p>
                        </div>
                        <p className="font-mono-meta text-[9px] text-white/30 mt-6 tracking-wide">
                            A WELCOME NOTE HAS BEEN SENT TO {membership.email.toUpperCase()}
                        </p>
                    </Reveal>
                ) : (
                    <Reveal>
                        <div className="text-center mb-8">
                            <h2 className="font-serif-display text-4xl font-medium">Become a member</h2>
                            <p className="text-sm text-white/50 mt-3">
                                {selectedPlan && plans
                                    ? `Joining as ${plans.find((p) => p.id === selectedPlan)?.name} — ${formatPrice(plans.find((p) => p.id === selectedPlan)?.price_monthly || 0)}/month`
                                    : "Choose a tier above to begin."}
                            </p>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4" data-testid="club-signup-form">
                            <div className="grid grid-cols-2 gap-4">
                                <input required placeholder="First name" value={form.first_name} onChange={set("first_name")} className={inputClass} data-testid="club-first-name-input" />
                                <input required placeholder="Last name" value={form.last_name} onChange={set("last_name")} className={inputClass} data-testid="club-last-name-input" />
                            </div>
                            <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={inputClass} data-testid="club-email-input" />
                            <button type="submit" disabled={submitting} className="btn-luxe btn-gold w-full !py-4 disabled:opacity-60" data-testid="club-join-button">
                                {submitting ? "Joining…" : "Join The Club"}
                            </button>
                            <p className="font-mono-meta text-[9px] text-white/30 text-center tracking-wide">
                                DEMO MEMBERSHIP — NO PAYMENT WILL BE TAKEN. BILLING ARRIVES WITH LAUNCH.
                            </p>
                        </form>
                    </Reveal>
                )}
            </section>
        </main>
    );
}
